<template>
  <div class="insights-overview-widgets">
    <!-- Row 1: Members Location (full width) -->
    <InsightsMembersLocationWidget
      v-if="remoteVsOffice"
      ref="membersLocation"
    />

    <!-- Row 2: Utilization | Work Time Classification -->
    <InsightsUtilizationWidget ref="utilization" />
    <InsightsWorkTimeClassificationWidget ref="workTimeClassification" />

    <!-- Row 3: Focus Time | Activity -->
    <InsightsFocusTimeWidget ref="focusTime" />
    <InsightsActivityWidget ref="activity" />

    <!-- Row 4: Start/Stop Time | Idle Time -->
    <InsightsStartStopTimeWidget
      v-if="showRvoWidgets"
      ref="startStopTime"
    />
    <InsightsIdleTimeWidget
      v-if="showRvoWidgets"
      ref="idleTime"
    />

    <!-- Row 5: Leaderboard (full width) -->
    <InsightsLeaderboardWidget
      v-if="isTeamDashboard && canSeeTeam"
      ref="leaderboard"
    />

    <!-- Row 6: Timeline (full width) -->
    <InsightsTimelineWidget
      v-if="!isTeamDashboard"
      ref="timeline"
    />

    <!-- Row 7: Meetings (full width) -->
    <InsightsMeetingsWidget ref="meetings" />

    <!-- Row 8: Expenditure (full width) -->
    <InsightsWorkTimeExpenditureWidget ref="workTimeExpenditure" />

    <!-- Row 9: Collaboration | Highlights -->
    <InsightsTeamCollaborationWidget ref="collaborations" />
    <InsightsHighlightsWidget ref="highlights" />

    <!-- Row 10: Rankings -->
    <InsightsRankingsWidget ref="rankings" />
  </div>
</template>

<script>
import InsightsMembersLocationWidget from '~/components/insights/InsightsMembersLocationWidget.vue';
import InsightsWorkTimeClassificationWidget from '~/components/insights/InsightsWorkTimeClassificationWidget.vue';
import InsightsUtilizationWidget from '~/components/insights/InsightsUtilizationWidget.vue';
import InsightsActivityWidget from '~/components/insights/InsightsActivityWidget.vue';
import InsightsHighlightsWidget from '~/components/insights/InsightsHighlightsWidget.vue';
import InsightsFocusTimeWidget from '~/components/insights/InsightsFocusTimeWidget.vue';
import InsightsLeaderboardWidget from '~/components/insights/InsightsLeaderboardWidget.vue';
import InsightsMeetingsWidget from '~/components/insights/InsightsMeetingsWidget.vue';
import InsightsWorkTimeExpenditureWidget from '~/components/insights/InsightsWorkTimeExpenditureWidget.vue';
import InsightsTeamCollaborationWidget from '~/components/insights/InsightsTeamCollaborationWidget.vue';
import InsightsIdleTimeWidget from '~/components/insights/InsightsIdleTimeWidget.vue';
import InsightsTimelineWidget from '~/components/insights/InsightsTimelineWidget.vue';
import InsightsRankingsWidget from '~/components/insights/InsightsRankingsWidget.vue';
import InsightsStartStopTimeWidget from '~/components/insights/InsightsStartStopTimeWidget.vue';

import { mapActions, mapGetters } from 'vuex';
import { INSIGHTS } from '~/app_constants';

// The widget identifiers each grid child renders, so a deferred widget can be requested as its own
// element comes into view. Identifiers match the ones the store keys its loading flags by.
const WIDGET_IDENTIFIERS = {
  membersLocation: [INSIGHTS.api.membersLocation],
  utilization: [INSIGHTS.api.utilization],
  workTimeClassification: [INSIGHTS.api.highlights.workTimeClassification],
  focusTime: [INSIGHTS.api.focusTime],
  activity: [INSIGHTS.api.highlights.activity],
  startStopTime: [INSIGHTS.api.startStopTime],
  idleTime: [INSIGHTS.api.idleTime],
  leaderboard: [INSIGHTS.api.leaderboard],
  timeline: [INSIGHTS.api.timeline],
  meetings: [INSIGHTS.api.meetings],
  workTimeExpenditure: [INSIGHTS.api.workTimeExpenditure],
  collaborations: [INSIGHTS.api.collaborations],
  highlights: [INSIGHTS.api.highlights.tools],
  rankings: [INSIGHTS.api.ranking.projects, INSIGHTS.api.ranking.tasks, INSIGHTS.api.ranking.categories],
};
// Start the request slightly before the widget reaches the viewport, so it is rarely seen loading.
const VIEWPORT_MARGIN = '100px';

export default {
  name: 'InsightsOverviewWidgets',
  components: {
    InsightsRankingsWidget,
    InsightsTimelineWidget,
    InsightsMembersLocationWidget,
    InsightsWorkTimeClassificationWidget,
    InsightsUtilizationWidget,
    InsightsActivityWidget,
    InsightsHighlightsWidget,
    InsightsFocusTimeWidget,
    InsightsLeaderboardWidget,
    InsightsMeetingsWidget,
    InsightsWorkTimeExpenditureWidget,
    InsightsTeamCollaborationWidget,
    InsightsIdleTimeWidget,
    InsightsStartStopTimeWidget,
  },
  computed: {
    ...mapGetters(['remoteVsOffice', 'showRvoWidgets', 'isTeamDashboard', 'canSeeTeam']),
  },
  created() {
    this.identifiersByElement = new Map();
    this.observer = null;
  },
  mounted() {
    this.observeWidgets();
  },
  // Widgets appear and disappear with the dashboard type and the remote-vs-office toggle, so pick up
  // the ones that were not in the DOM on mount.
  updated() {
    this.observeWidgets();
  },
  beforeUnmount() {
    this.observer?.disconnect();
  },
  methods: {
    ...mapActions(['setWidgetVisibility', 'fetchAllPendingWidgets']),
    observeWidgets() {
      if (!window.IntersectionObserver) {
        this.fetchAllPendingWidgets();

        return;
      }
      if (!this.observer) {
        this.observer = new IntersectionObserver(this.onIntersection, { rootMargin: VIEWPORT_MARGIN });
      }

      Object.entries(WIDGET_IDENTIFIERS).forEach(([ref, identifiers]) => {
        const element = this.$refs[ref]?.$el;

        if (element && !this.identifiersByElement.has(element)) {
          this.identifiersByElement.set(element, identifiers);
          this.observer.observe(element);
        }
      });
    },
    onIntersection(entries) {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return;
        }

        this.identifiersByElement.get(target)?.forEach(this.setWidgetVisibility);
        this.observer.unobserve(target);
        this.identifiersByElement.delete(target);
      });
    },
  },
};
</script>
